import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import Footer from "../../Components/Footer/Footer";
import ReuseablePopup from "../../Components/ReuseablePopups/ReuseablePopup";
import RegisterNewCourseHeroSection from "./HeroSection/HeroSection";
import LicenseTypeSelection from "./LicenseTypeSelection/LicenseTypeSelection";
import LearnerInformation from "./LearnerInformation/LearnerInformation";
import PaymentSummary from "./PaymentSummary/PaymentSummary";
import PaymentMethods from "./PaymentMethods/PaymentMethods";
import { SaveSpecialPromotionLead } from "../../Actions/SpecialPromotionA";
import bdePackages from "../../JsonFiles/RegisterNewCoursePackages.json";
import inVehicleOptions from "../../JsonFiles/NewCourseInvehicleTrainingOptions.json";
import driveTestOptions from "../../JsonFiles/NewCourseDriveTestOptions.json";
import congratulationsImage from "./Assets/CongratulationsImage.png";

const PAGE_URL = "https://vehiclelearning.com/register/course";
const PAGE_TITLE =
  "Register for Your BDE Driving Course | Vehicle Learning Innovation";
const PAGE_DESCRIPTION =
  "Register online for VLI's MTO-approved BDE certificate course. Choose your package and complete your registration.";
const OG_IMAGE = "https://vehiclelearning.com/assets/register-course-hero.png";

const initialSelectionState = {
  licenseType: "G2",
  sections: {
    bde: {
      checked: true,
      packageId: 1,
    },
    inVehicle: {
      checked: false,
      optionIndex: null,
      customHours: "",
    },
    driveTest: {
      checked: false,
      optionIndex: null,
    },
  },
};

const initialLearnerInfo = {
  fullName: "",
  email: "",
  confirmEmail: "",
  phone: "",
  address: "",
  notes: "",
  startDate: "",
  guardianContact: "",
  licenseFront: null,
  licenseBack: null,
  /** Honeypot — must stay empty; bots often fill it */
  website: "",
};

function isMeaningfulPhone(phone) {
  const digits = String(phone || "").replace(/\D/g, "");
  // Need a real number, not just dial code (e.g. "1" / "91")
  return digits.length >= 7;
}

function buildSpecialPromotionLeadPayload(
  learnerInfo,
  selectionState,
  userId,
  instituteCourseId
) {
  const email = (learnerInfo.email || "").trim();
  const phone = isMeaningfulPhone(learnerInfo.phone)
    ? String(learnerInfo.phone).trim()
    : "";

  if (!email && !phone) return null;

  const { licenseType, sections } = selectionState || {};
  const { bde, inVehicle, driveTest } = sections || {};

  let bdePackageName = null;
  let bdePackagePrice = null;
  if (bde?.checked && bde?.packageId) {
    const pkg = bdePackages.find((p) => p.id === bde.packageId);
    bdePackageName = pkg?.name || null;
    bdePackagePrice = pkg?.price ?? null;
  }

  let inVehicleTrainingPrice = null;
  let inVehicleOptionName = null;
  let inVehicleCustomHours = null;
  if (inVehicle?.checked && inVehicle.optionIndex != null) {
    const opt = inVehicleOptions[inVehicle.optionIndex];
    if (opt?.custom) {
      const hours = Number(inVehicle.customHours);
      inVehicleCustomHours = Number.isNaN(hours) ? null : hours;
      inVehicleTrainingPrice =
        hours >= 2 ? Number((hours * 50).toFixed(2)) : null;
      inVehicleOptionName = "Custom hours";
    } else {
      inVehicleTrainingPrice = opt?.price ?? null;
      inVehicleOptionName = opt?.hours != null ? `${opt.hours} hours` : null;
    }
  }

  let driveTestPrice = null;
  let driveTestOptionName = null;
  if (driveTest?.checked && driveTest.optionIndex != null) {
    const opt = driveTestOptions[driveTest.optionIndex];
    driveTestPrice = opt?.price ?? null;
    driveTestOptionName = opt?.label || null;
  }

  const totalAmount = [bdePackagePrice, inVehicleTrainingPrice, driveTestPrice]
    .filter((v) => v != null && !Number.isNaN(Number(v)))
    .reduce((sum, v) => sum + Number(v), 0);

  return {
    fullName: (learnerInfo.fullName || "").trim(),
    email,
    phone,
    userId: userId || null,
    instituteCourseId: instituteCourseId || null,
    licenseType: licenseType || null,
    bdePackageName,
    bdePackagePrice,
    driveTestPrice,
    driveTestOptionName,
    inVehicleTrainingPrice,
    inVehicleOptionName,
    inVehicleCustomHours,
    totalAmount: totalAmount || null,
    notes: (learnerInfo.notes || "").trim() || null,
    guardianContact: (learnerInfo.guardianContact || "").trim() || null,
    preferredStartDate: (learnerInfo.startDate || "").trim() || null,
    website: learnerInfo.website || "",
  };
}

const RegisterNewCourse = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { UserInfo, Authenticated } = useSelector(
    (store) => store.LoginSignupReducer
  );

  const [selectionState, setSelectionState] = useState(initialSelectionState);
  const [learnerInfo, setLearnerInfo] = useState(initialLearnerInfo);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState("");
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [successPaymentMethod, setSuccessPaymentMethod] = useState("");
  const learnerFormRef = useRef(null);
  const paymentSummaryRef = useRef(null);
  const leadPayloadRef = useRef(null);
  const leadFlushingRef = useRef(false);

  const userId = Authenticated ? UserInfo?.UserId || UserInfo?.id || null : null;
  const instituteCourseId = location.state?.InstituteCourseId || null;

  // Keep latest lead payload in a ref (safe for unload handlers)
  useEffect(() => {
    leadPayloadRef.current = buildSpecialPromotionLeadPayload(
      learnerInfo,
      selectionState,
      userId,
      instituteCourseId
    );
  }, [learnerInfo, selectionState, userId, instituteCourseId]);

  // On refresh / close / tab hide / leave page → save lead if email or phone present
  useEffect(() => {
    const flushLead = (useBeacon = false) => {
      const payload = leadPayloadRef.current;
      if (!payload) return;
      if (!payload.email && !payload.phone) return;
      if (leadFlushingRef.current) return;

      leadFlushingRef.current = true;
      dispatch(SaveSpecialPromotionLead(payload, { useBeacon }));

      setTimeout(() => {
        leadFlushingRef.current = false;
      }, 1500);
    };

    const onPageHide = () => flushLead(true);
    const onVisibilityChange = () => {
      if (document.visibilityState === "hidden") flushLead(true);
    };

    window.addEventListener("pagehide", onPageHide);
    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      window.removeEventListener("pagehide", onPageHide);
      document.removeEventListener("visibilitychange", onVisibilityChange);
      // React Router navigation away from this page
      flushLead(false);
    };
  }, [dispatch]);

  const handleBeforeSelectPaymentMethod = () => {
    const isValid = learnerFormRef.current?.validate?.() ?? false;
    if (!isValid) {
      document
        .getElementById("register-new-course-learner-form")
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
    return isValid;
  };

  const checkoutBase = useMemo(() => {
    const lead = buildSpecialPromotionLeadPayload(
      learnerInfo,
      selectionState,
      userId,
      instituteCourseId
    );
    return {
      fullName: learnerInfo.fullName || "",
      email: (learnerInfo.email || "").trim(),
      phone: learnerInfo.phone || "",
      address: learnerInfo.address || "",
      notes: learnerInfo.notes || "",
      guardianContact: learnerInfo.guardianContact || "",
      licenseType: selectionState.licenseType,
      instituteCourseId,
      bdePackageName: lead?.bdePackageName || null,
      bdePackagePrice: lead?.bdePackagePrice ?? null,
      driveTestPrice: lead?.driveTestPrice ?? null,
      driveTestOptionName: lead?.driveTestOptionName || null,
      inVehicleTrainingPrice: lead?.inVehicleTrainingPrice ?? null,
      inVehicleOptionName: lead?.inVehicleOptionName || null,
      inVehicleCustomHours: lead?.inVehicleCustomHours ?? null,
      preferredStartDate: learnerInfo.startDate || null,
      licenseFront: learnerInfo.licenseFront,
      licenseBack: learnerInfo.licenseBack,
    };
  }, [learnerInfo, selectionState, userId, instituteCourseId]);

  const showEnrollmentForm = selectionState.licenseType !== "G1";
  const anyServiceSelected = Boolean(
    selectionState?.sections?.bde?.checked ||
      selectionState?.sections?.inVehicle?.checked ||
      selectionState?.sections?.driveTest?.checked
  );

  const courseSchemaGraph = useMemo(
    () =>
      (bdePackages || []).map((pkg) => ({
        "@type": "Course",
        "@id": `${PAGE_URL}#course-${pkg.id}`,
        name: pkg.name,
        description: `${pkg.name} — MTO-approved Beginner Driver Education (BDE) package including ${(pkg.features || []).join(", ")}.`,
        provider: {
          "@type": "Organization",
          name: "Vehicle Learning Innovation",
          url: "https://vehiclelearning.com",
        },
        offers: {
          "@type": "Offer",
          url: PAGE_URL,
          price: Number(pkg.price),
          priceCurrency: "CAD",
          availability: "https://schema.org/InStock",
          category: "Beginner Driver Education",
        },
        educationalCredentialAwarded: "MTO BDE Certificate",
        about: "Ontario G2 / G Beginner Driver Education",
        inLanguage: "en",
        isAccessibleForFree: false,
        hasCourseInstance: {
          "@type": "CourseInstance",
          courseMode: ["online", "onsite"],
          courseWorkload: (pkg.features || []).find((f) =>
            /Hours/i.test(f)
          ),
        },
      })),
    []
  );

  return (
    <>
      <Helmet prioritizeSeoTags>
        <title>{PAGE_TITLE}</title>
        <meta name="description" content={PAGE_DESCRIPTION} />
        <meta
          name="keywords"
          content="BDE driving course registration, MTO approved BDE certificate, register driving course Ontario, G2 G test preparation, Vehicle Learning Innovation"
        />
        <link rel="canonical" href={PAGE_URL} />

        <meta property="og:title" content={PAGE_TITLE} />
        <meta property="og:description" content={PAGE_DESCRIPTION} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={PAGE_URL} />
        <meta property="og:image" content={OG_IMAGE} />
        <meta
          property="og:image:alt"
          content="Vehicle Learning Innovation training vehicle — register for your BDE driving course"
        />
        <meta property="og:image:type" content="image/png" />
        <meta property="og:locale" content="en_CA" />
        <meta property="og:site_name" content="Vehicle Learning Innovation" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={PAGE_TITLE} />
        <meta name="twitter:description" content={PAGE_DESCRIPTION} />
        <meta name="twitter:image" content={OG_IMAGE} />

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            name: PAGE_TITLE,
            description: PAGE_DESCRIPTION,
            url: PAGE_URL,
            isPartOf: {
              "@type": "WebSite",
              name: "Vehicle Learning Innovation",
              url: "https://vehiclelearning.com",
            },
            about: {
              "@type": "Thing",
              name: "Beginner Driver Education (BDE) Course Registration",
            },
          })}
        </script>

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@graph": courseSchemaGraph,
          })}
        </script>
      </Helmet>

      {/* Crawlable main copy once React renders (Google executes JS) */}
      <p className="sr-only">
        {PAGE_DESCRIPTION} Available BDE packages:{" "}
        {(bdePackages || []).map((pkg) => pkg.name).join(", ")}.
      </p>

      <RegisterNewCourseHeroSection />
      <LicenseTypeSelection
        selectionState={selectionState}
        setSelectionState={setSelectionState}
      />

      {showEnrollmentForm && (
        <>
          <div className="w-full flex flex-col gap-[10px] justify-center items-center h-[170px] bg-[linear-gradient(90deg,_#382456_0%,_#582E92_100%)]">
            <p className="text-[22px] font-semibold text-[#fff] text-center">
              Personal Information
            </p>
            <p className="text-[18px] font-bold text-[#fff] text-center">
              Input your demographic details to set up your account profile.
            </p>
          </div>

          <div id="register-new-course-learner-form">
            <LearnerInformation
              ref={learnerFormRef}
              selectionState={selectionState}
              learnerInfo={learnerInfo}
              setLearnerInfo={setLearnerInfo}
            />
          </div>

          {anyServiceSelected && (
            <>
              <div className="w-full flex flex-col gap-[10px] justify-center items-center h-[170px] bg-[linear-gradient(90deg,_#382456_0%,_#582E92_100%)]">
                <p className="text-[22px] font-semibold text-[#fff] text-center">
                  Payment Method & Checkout
                </p>
                <p className="text-[18px] font-bold text-[#fff] text-center">
                  Complete your enrollment by choosing a safe, reliable payment
                  method below to activate your account.
                </p>
              </div>

              <div ref={paymentSummaryRef}>
                <PaymentSummary selectionState={selectionState} />
              </div>

              <PaymentMethods
                selectionState={selectionState}
                selectedPaymentMethod={selectedPaymentMethod}
                setSelectedPaymentMethod={setSelectedPaymentMethod}
                onBeforeSelectPaymentMethod={handleBeforeSelectPaymentMethod}
                checkoutBase={checkoutBase}
                onCheckoutSuccess={(paymentMethod) => {
                  setSuccessPaymentMethod(paymentMethod || "");
                  setShowSuccessPopup(true);
                }}
              />
            </>
          )}
        </>
      )}

      {showSuccessPopup && (
        <ReuseablePopup
          setShowPopup={() => {
            setShowSuccessPopup(false);
            navigate("/");
          }}
          heading="Welcome to Vehicle Learning Innovation!"
          text={
            successPaymentMethod === "interacETransfer"
              ? "We’ll verify your e-Transfer payment and contact you via email shortly once it’s confirmed."
              : "Your registration and payment were successful. Keep an eye on your inbox we will reach out to you very soon with everything you need to start your training."
          }
          btnText="Done"
          onClick={() => {
            setShowSuccessPopup(false);
            navigate("/");
          }}
          imageSrc={congratulationsImage}
          imageAlt="Registration successful celebration graphic"
        />
      )}

      <Footer />
    </>
  );
};

export default RegisterNewCourse;
